import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { obtenerPaisPorIp } from "../Servicios/geolocalizacionService";
import PAISES_SOPORTADOS from "../paisesSoportados";

// Contexto global de regionalización: expone el país (detectado por IP o
// elegido manualmente por el usuario) para que cualquier funcionalidad,
// presente o futura (footer, textos, contactos, promociones, etc.), lo
// reutilice sin volver a detectar ni duplicar el estado.

const STORAGE_KEY = "paisDetectado";
const STORAGE_TTL_MS = 24 * 60 * 60 * 1000; // 24 horas

const PaisContext = createContext({
  pais: null,
  cargando: true,
  seleccionManual: false,
  establecerPais: () => {},
});

// Ahora que existe un selector manual de país, un país sin soporte (ej. la
// IP geolocalizó a un país fuera de PAISES_SOPORTADOS) o una detección
// fallida (null) ya no dejan a la app "sin país" — caen al primero de
// PAISES_SOPORTADOS (Guatemala) para que el combo y el resto de la app
// siempre tengan un valor válido con el que arrancar; el usuario lo puede
// cambiar de inmediato si no es el correcto.
function normalizarPais(codigoPais) {
  const esSoportado = PAISES_SOPORTADOS.some((p) => p.codigo === codigoPais);
  return esSoportado ? codigoPais : PAISES_SOPORTADOS[0].codigo;
}

function leerPaisCacheado() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const datos = JSON.parse(raw);
    if (!datos || !datos.pais || !datos.timestamp) return null;
    // Una selección manual del usuario no vence por TTL: es una decisión
    // explícita, más confiable que una detección automática por IP.
    if (!datos.manual && Date.now() - datos.timestamp > STORAGE_TTL_MS) return null;
    return datos;
  } catch (error) {
    return null;
  }
}

function guardarPaisCache(pais, manual) {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ pais, timestamp: Date.now(), manual })
    );
  } catch (error) {
    // localStorage no disponible (modo privado, cuota llena, etc.): se ignora,
    // la detección/selección simplemente no persiste entre cargas.
  }
}

export function PaisProvider({ children }) {
  const [pais, setPais] = useState(() => {
    const cache = leerPaisCacheado();
    return cache ? normalizarPais(cache.pais) : null;
  });
  const [seleccionManual, setSeleccionManual] = useState(() => {
    const cache = leerPaisCacheado();
    return Boolean(cache && cache.manual);
  });
  const [cargando, setCargando] = useState(() => leerPaisCacheado() === null);

  useEffect(() => {
    if (pais !== null) return;

    let activo = true;
    obtenerPaisPorIp().then((paisDetectado) => {
      // No se cachea un resultado nulo/no soportado tal cual: si la API
      // falló, se reintenta en la próxima carga en lugar de guardar un
      // "GT" que no fue realmente detectado. El estado en memoria sí se
      // normaliza (más abajo) para que la app siempre arranque con un
      // país válido con el que trabajar.
      if (paisDetectado) {
        guardarPaisCache(paisDetectado, false);
      }
      if (activo) {
        setPais(normalizarPais(paisDetectado));
        setCargando(false);
      }
    });

    return () => {
      activo = false;
    };
  }, [pais]);

  // Selección explícita del usuario (ej. combo de país antes del login):
  // toma efecto de inmediato en todo lo que consuma usePais() — Footer.js
  // y cualquier módulo futuro — sin recargar la página, y persiste para
  // que la próxima carga respete la elección en vez de re-detectar por IP.
  const establecerPais = useCallback((codigoPais) => {
    const paisValido = normalizarPais(codigoPais);
    guardarPaisCache(paisValido, true);
    setPais(paisValido);
    setSeleccionManual(true);
    setCargando(false);
  }, []);

  return (
    <PaisContext.Provider value={{ pais, cargando, seleccionManual, establecerPais }}>
      {children}
    </PaisContext.Provider>
  );
}

export function usePais() {
  return useContext(PaisContext);
}

export default PaisContext;
